import type { DexStatus, DexCategory } from "@/lib/data";
import { Lock, Sparkles, Star } from "lucide-react";
import { PokemonSilhouette } from "./PokemonSilhouette";
import { findPokemon } from "@/lib/pokemon";

interface PokemonSpriteProps {
  id: number;
  status: DexStatus;
  category?: DexCategory;
  size?: number;
  isNew?: boolean;
  /** Retained for API compatibility; silhouettes do not have an HQ variant. */
  hq?: boolean;
  showLabel?: boolean;
  /** Render a Mega/G-Max form silhouette (e.g. "6-mega-x"); overrides id. */
  formSlug?: string;
  className?: string;
}

/**
 * PokemonSprite — silhouette-only renderer for any species + dex state.
 *
 * Per project policy we never ship colour artwork. This component is a thin
 * presentational shell around <PokemonSilhouette/> which renders a tile from
 * the bundled atlas (built from MIT pokesprite source, see
 * scripts/silhouettes/build.ts). All status / category treatments are applied
 * as outer ring, badge, and label overlays — the silhouette itself is the
 * single source of species identity.
 *
 * Status visual rules:
 *   caught  → brand-tinted silhouette (green by default, gold for shiny),
 *             subtle ring
 *   seen    → neutral mid-grey silhouette, "SEEN" tag
 *   missing → neutral dark silhouette, "MISSING" tag
 *   locked  → neutral dark silhouette with padlock, "NOT IN GO" tag
 *
 * Category accents:
 *   shiny   → gold tint + sparkle (only meaningful with caught)
 *   lucky   → animated gold border ring (caught only)
 *   perfect → pink star badge (caught only)
 */
export function PokemonSprite({
  id,
  status,
  category = "main",
  size = 56,
  isNew,
  hq: _hq = false,
  showLabel = false,
  formSlug,
  className = "",
}: PokemonSpriteProps) {
  const px = size;
  const useShiny = category === "shiny" && status === "caught";
  const species = findPokemon(id);
  const speciesName = species?.name ?? `Species ${id}`;
  const isCaught = status === "caught";
  const variant: "default" | "locked" = status === "locked" ? "locked" : "default";

  return (
    <div
      className={`pokemon-sprite relative inline-flex items-center justify-center ${className}`}
      style={{ width: px, height: px }}
      data-status={status}
      data-category={category}
    >
      {/* Lucky border ring (caught only) */}
      {category === "lucky" && isCaught && (
        <span
          aria-hidden
          className="absolute inset-0 rounded-lg pointer-events-none"
          style={{
            background:
              "conic-gradient(from 0deg, hsl(43 96% 60%), hsl(43 96% 75%), hsl(43 96% 60%), hsl(43 96% 80%), hsl(43 96% 60%))",
            padding: 2,
            mask: "linear-gradient(#000 0 0) content-box, linear-gradient(#000 0 0)",
            WebkitMask: "linear-gradient(#000 0 0) content-box, linear-gradient(#000 0 0)",
            WebkitMaskComposite: "xor",
            maskComposite: "exclude",
            filter: "drop-shadow(0 0 6px hsl(43 96% 60% / 0.55))",
          }}
        />
      )}

      {/*
        THE TRAINER'S OWN SCAN CROP USED TO RENDER HERE, in a round plate with a
        neon ring, in place of the ordinary art. It is gone (2026-08-13). The
        crop was a low-resolution lift off a phone screenshot, so a tile got a
        murky little sprite where its neighbour had a clean render — and which
        one you saw depended on nothing a reader could see, namely whether that
        species happened to have been scanned. The round plate made the
        inconsistency louder, not quieter.

        Nothing captures crops any more either (see dexPipelineRunner), so there
        is nothing new to read: this only ever showed art from before that
        change. One source of art, everywhere.
      */}
      <div
        className={`pokemon-sprite__plate flex items-center justify-center w-full h-full overflow-hidden rounded-lg ${
          isCaught ? "is-caught" : "is-uncaught"
        }`}
      >
        <PokemonSilhouette
          id={id}
          size={Math.round(px * 0.96)}
          caught={isCaught}
          shiny={useShiny}
          variant={variant}
          formSlug={formSlug}
          ariaLabel={speciesName}
        />
      </div>

      {/* Lock icon overlay for not-in-go */}
      {status === "locked" && (
        <Lock
          className="absolute text-muted-foreground/85"
          style={{ width: px * 0.28, height: px * 0.28 }}
          strokeWidth={2.4}
          aria-hidden
        />
      )}

      {/* Shiny sparkle (caught only) */}
      {category === "shiny" && isCaught && (
        <Sparkles
          aria-hidden
          className="absolute -top-1 -right-1 text-[hsl(var(--shiny))]"
          style={{
            width: px * 0.28,
            height: px * 0.28,
            filter: "drop-shadow(0 0 4px hsl(var(--shiny) / 0.7))",
          }}
        />
      )}

      {/* Perfect pink star badge */}
      {category === "perfect" && isCaught && (
        <span
          aria-hidden
          className="absolute -top-1 -right-1 rounded-full flex items-center justify-center"
          style={{
            width: px * 0.36,
            height: px * 0.36,
            background: "linear-gradient(135deg, hsl(326 92% 78%), hsl(326 92% 60%))",
            boxShadow: "0 0 8px hsl(326 92% 70% / 0.6)",
          }}
        >
          <Star
            className="text-white"
            style={{ width: px * 0.20, height: px * 0.20, fill: "white" }}
            strokeWidth={2}
          />
        </span>
      )}

      {/* Status label */}
      {showLabel && (status === "seen" || status === "missing" || status === "locked") && (
        <span
          className="absolute -bottom-1.5 left-1/2 -translate-x-1/2 px-1 py-px rounded text-[7px] font-bold uppercase tracking-wider whitespace-nowrap"
          style={{
            background:
              status === "seen"
                ? "hsl(220 12% 65% / 0.18)"
                : status === "missing"
                ? "hsl(var(--ai) / 0.18)"
                : "hsl(225 14% 30% / 0.32)",
            color:
              status === "seen"
                ? "hsl(220 12% 80%)"
                : status === "missing"
                ? "hsl(var(--ai))"
                : "hsl(225 14% 70%)",
            border:
              status === "seen"
                ? "0.5px solid hsl(220 12% 65% / 0.28)"
                : status === "missing"
                ? "0.5px solid hsl(var(--ai) / 0.32)"
                : "0.5px solid hsl(225 14% 30% / 0.4)",
          }}
        >
          {status === "locked" ? "NOT IN GO" : status}
        </span>
      )}

      {/* NEW badge */}
      {isNew && (
        <span
          className="absolute -top-1 -left-1 rounded-md px-1 py-px text-[8px] font-bold tracking-wider uppercase"
          style={{ background: "linear-gradient(135deg, #5EE2F0, #2BB8C9)", color: "#0B0E1A" }}
        >
          NEW
        </span>
      )}
    </div>
  );
}
